import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { FiCheck, FiStar, FiArrowLeft, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import SEO from '../components/SEO';
import PageTransition from '../components/PageTransition';
import SafeIcon from '../common/SafeIcon';
import SkeletonBlock from '../components/skeletons/SkeletonBlock';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { PRO_FEATURES } from '../config/plans';
import {
  getOfferings,
  purchasePackage,
  restorePurchases,
  isRevenueCatAvailable,
  openManagementUrl,
  isNativeIOS,
} from '../services/revenueCatService';

// Default fallback copy shown when the RevenueCat SDK isn't configured yet
// (e.g. local dev before keys are wired up). Real product titles, prices,
// and intro periods come from the RC offerings catalogue. We never *charge*
// from these fallbacks - `purchasePackage` requires a real RC package.
const FALLBACK_PRICES = {
  monthly: { id: 'teesync_pro_monthly', label: 'Monthly', price: '£2.49', period: '/month' },
  annual:  { id: 'teesync_pro_annual',  label: 'Annual',  price: '£14.99', period: '/year', savingsHint: 'Save 50%' },
};

// Normalise a Package's product across web (purchases-js) and Capacitor
// (purchases-capacitor) SDK shapes. Web uses pkg.webBillingProduct with
// `currentPrice.formattedPrice`; Capacitor uses pkg.product with
// `priceString` and `subscriptionPeriod`. We pull what we need from whichever
// shape is present and fall back to the static fallback copy if neither has
// it (e.g. when the SDK isn't configured yet).
//
// On iOS native, RC's Capacitor SDK can hand back a package that still
// carries the dashboard's web billing product (whose currentPrice is the
// USD dashboard default) alongside the real StoreKit product. We prefer
// the StoreKit-localised priceString in that case so UK customers don't
// see dollars on the cards while the native purchase sheet quotes GBP.
const productView = (pkg, fallback) => {
  const web = pkg?.webBillingProduct;
  const cap = pkg?.product;
  const preferCap = Capacitor.isNativePlatform();

  const fromCap = () => ({
    label: cap?.title || fallback.label,
    price: cap?.priceString || fallback.price,
    period: cap?.subscriptionPeriod
      ? (cap.subscriptionPeriod.toUpperCase().includes('Y') ? '/year' : '/month')
      : fallback.period,
  });

  const fromWeb = () => {
    const unit = web?.period?.unit || '';
    return {
      label: web?.title || web?.displayName || fallback.label,
      price: web?.currentPrice?.formattedPrice || fallback.price,
      period: unit === 'year' ? '/year' : unit === 'month' ? '/month' : fallback.period,
    };
  };

  if (preferCap && cap) return fromCap();
  if (web) return fromWeb();
  if (cap) return fromCap();
  return { label: fallback.label, price: fallback.price, period: fallback.period };
};

function PackageCardSkeleton({ withBadge = false }) {
  return (
    <div
      className="relative w-full rounded-xl border-2 border-border-subtle p-4"
      aria-hidden="true"
    >
      {withBadge && (
        <SkeletonBlock className="absolute -top-2 right-3 h-4 w-14 rounded-full" />
      )}
      <div className="flex items-center justify-between">
        <div>
          <SkeletonBlock className="h-4 w-20 rounded-md" />
          <SkeletonBlock className="mt-1 h-3 w-14 rounded-md" />
        </div>
        <div className="flex flex-col items-end">
          <SkeletonBlock className="h-5 w-16 rounded-md" />
          <SkeletonBlock className="mt-1 h-3 w-10 rounded-md" />
        </div>
      </div>
    </div>
  );
}

function PackageCard({ pkg, fallback, selected, onSelect }) {
  const { label, price, period } = productView(pkg, fallback);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative w-full text-left rounded-xl border-2 p-4 transition-all ${
        selected
          ? 'border-accent bg-accent/5 shadow-sm'
          : 'border-border-subtle hover:border-accent/60'
      }`}
    >
      {fallback.savingsHint && (
        <span className="absolute -top-2 right-3 bg-accent text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
          {fallback.savingsHint}
        </span>
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-text-primary">{label}</div>
          <div className="mt-0.5 text-xs text-text-secondary">Pro plan</div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-text-primary">{price}</div>
          <div className="text-xs text-text-secondary">{period}</div>
        </div>
      </div>
    </button>
  );
}

function Upgrade() {
  const navigate = useNavigate();
  const { user, isPro, planSource, planPeriodEnd, refreshPlan } = useAuth();
  const toast = useToast();
  const isOnNative = Capacitor.isNativePlatform();

  const [offering, setOffering] = useState(null);
  const [loadingOffering, setLoadingOffering] = useState(true);
  const [selected, setSelected] = useState('annual');
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [managingSubscription, setManagingSubscription] = useState(false);

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    try {
      const opened = await openManagementUrl();
      if (!opened) {
        toast.info('Subscription management isn’t available yet - please contact support.');
      }
    } finally {
      setManagingSubscription(false);
    }
  };

  // Branch the Pro-user "Manage" block by where the subscription was bought.
  // Same logic as the Settings card - keep parity so users see consistent
  // copy whether they arrived here via the Settings CTA or directly.
  const showManageButton = isPro && (
    (planSource === 'ios' && isOnNative) ||
    (planSource === 'stripe' && !isOnNative) ||
    (!planSource)
  );
  const showIosMismatch = isPro && planSource === 'ios' && !isOnNative;
  const showStripeMismatch = isPro && planSource === 'stripe' && isOnNative;
  const showManualNote = isPro && planSource === 'manual';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const current = await getOfferings();
        if (!cancelled) setOffering(current);
      } catch (err) {
        console.error('Failed to load offerings:', err);
      } finally {
        if (!cancelled) setLoadingOffering(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const monthlyPackage = offering?.monthly || offering?.availablePackages?.find?.(p => /month/i.test(p?.identifier || ''));
  const annualPackage  = offering?.annual  || offering?.availablePackages?.find?.(p => /annual|year/i.test(p?.identifier || ''));

  const handlePurchase = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }
    const pkg = selected === 'annual' ? annualPackage : monthlyPackage;
    if (!pkg) {
      toast.error('Purchases aren’t configured yet on this device. Please try again later.');
      return;
    }
    setPurchasing(true);
    try {
      await purchasePackage(pkg);
      // RevenueCat's webhook fires async after Apple/RC ACKs the receipt.
      // iOS sandbox can be slow - measured ~20s wall time between
      // StoreKit confirm and user_profiles.updated_at landing. Poll for up
      // to 60s and log progress so a stuck purchase is diagnosable.
      let snapshot = null;
      for (let i = 0; i < 60; i++) {
        snapshot = await refreshPlan();
        if (snapshot?.isPro) {
          console.log('[Upgrade] plan flipped to pro after', i * 1000, 'ms');
          break;
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (snapshot?.isPro) {
        toast.success('You’re Pro! Enjoy unlimited TeeSync.');
        navigate(-1);
      } else {
        console.warn('[Upgrade] plan still free after 60s poll - webhook did not land in window');
        toast.info(
          'Purchase received - Pro will activate within a minute. If it stays free, tap Restore Purchases.',
        );
      }
    } catch (err) {
      if (err?.userCancelled) return;
      // RC wraps inner Stripe Elements errors; the real cause is in
      // `underlyingErrorMessage`. Surface both so we can debug.
      console.error('Purchase failed:', {
        message: err?.message,
        underlying: err?.underlyingErrorMessage,
        code: err?.errorCode,
        err,
      });
      const detail = err?.underlyingErrorMessage;
      const headline = err?.message || 'Purchase failed. Please try again.';
      toast.error(detail ? `${headline}: ${detail}` : headline);
    } finally {
      setPurchasing(false);
    }
  };

  const isIOSNative = isOnNative && Capacitor.getPlatform() === 'ios';
  const restoreLabel = isIOSNative ? 'Restore Purchases' : 'Refresh subscription status';
  const restoreSuccessText = isIOSNative ? 'Purchases restored.' : 'Subscription status refreshed.';
  const restoreEmptyText = isIOSNative
    ? 'No purchases to restore.'
    : 'No subscription found to restore.';

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restorePurchases();
      await refreshPlan();
      toast.success(restoreSuccessText);
    } catch (err) {
      console.error('Restore failed:', err);
      toast.info(err?.message || restoreEmptyText);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <PageTransition>
      <SEO title="Upgrade to Pro · TeeSync" />
      <div className="min-h-screen bg-app">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="pt-2 pb-3 max-w-2xl mx-auto">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary"
          >
            <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
            Back
          </button>
        </header>

        <main className="pb-24 max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/10 mb-3">
              <SafeIcon icon={FiStar} className="w-7 h-7 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">
              {isPro ? 'You’re on Pro' : 'Upgrade to TeeSync Pro'}
            </h1>
            <p className="text-sm text-text-secondary">
              {isPro
                ? (planPeriodEnd
                  ? `Renews ${new Date(planPeriodEnd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                  : 'Thanks for supporting TeeSync. You have unlimited access to everything.')
                : 'Unlimited everything, on every device. Cancel anytime.'}
            </p>
          </div>

          {isPro && (
            <div className="bg-card rounded-xl p-5 shadow-sm mb-4 space-y-3">
              <h2 className="text-sm font-semibold text-text-primary">Manage your subscription</h2>

              {showManualNote && (
                <p className="text-sm text-text-secondary leading-relaxed">
                  Your Pro access was granted manually. Contact support to make changes.
                </p>
              )}

              {showIosMismatch && (
                <p className="text-sm text-text-secondary leading-relaxed">
                  Your subscription was purchased on iOS. To cancel or change your plan, open the TeeSync iOS app, or visit{' '}
                  <a
                    href="https://apps.apple.com/account/subscriptions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent underline inline-flex items-center gap-1"
                  >
                    apps.apple.com/account/subscriptions
                    <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                  </a>.
                </p>
              )}

              {showStripeMismatch && (
                <p className="text-sm text-text-secondary leading-relaxed">
                  Your subscription was purchased on the web. To cancel or change your plan, sign in to teesync.app from a browser.
                </p>
              )}

              {showManageButton && (
                <>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    {isOnNative
                      ? 'Cancel, change your plan, or update payment in the App Store.'
                      : 'Cancel, change your plan, update your card, or download invoices.'}
                  </p>
                  <button
                    type="button"
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                    className="w-full px-4 py-3 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-interactive-hover transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {managingSubscription ? 'Opening…' : 'Manage Subscription'}
                    {!managingSubscription && <SafeIcon icon={FiExternalLink} className="w-4 h-4" />}
                  </button>
                </>
              )}
            </div>
          )}

          {!isPro && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-xl p-5 shadow-sm mb-4 space-y-3"
            >
              {loadingOffering ? (
                <>
                  <PackageCardSkeleton withBadge />
                  <PackageCardSkeleton />
                </>
              ) : (
                <>
                  <PackageCard
                    pkg={annualPackage}
                    fallback={FALLBACK_PRICES.annual}
                    selected={selected === 'annual'}
                    onSelect={() => setSelected('annual')}
                  />
                  <PackageCard
                    pkg={monthlyPackage}
                    fallback={FALLBACK_PRICES.monthly}
                    selected={selected === 'monthly'}
                    onSelect={() => setSelected('monthly')}
                  />
                </>
              )}

              {(() => {
                const selectedPkg = selected === 'annual' ? annualPackage : monthlyPackage;
                const selectedFallback = selected === 'annual' ? FALLBACK_PRICES.annual : FALLBACK_PRICES.monthly;
                const view = productView(selectedPkg, selectedFallback);
                const periodSuffix = view.period === '/year' ? '/yr' : '/mo';
                return (
                  <button
                    type="button"
                    onClick={handlePurchase}
                    disabled={purchasing || loadingOffering}
                    className="w-full mt-2 px-4 py-3 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-interactive-hover transition-colors disabled:opacity-50"
                  >
                    {purchasing ? 'Processing…' : `Get Pro · ${view.price}${periodSuffix}`}
                  </button>
                );
              })()}

              {!isRevenueCatAvailable() && (
                <p className="text-xs text-status-warning text-center">
                  Purchases aren’t configured in this build yet. (Set VITE_REVENUECAT_PUBLIC_KEY_WEB / _IOS.)
                </p>
              )}

              {/* Subscription disclosure block. On native iOS, Apple guideline
                  3.1.2(c) requires the title, length, price, auto-renew copy,
                  and functional Terms + Privacy links inside the purchase flow,
                  plus the Apple EULA. On web (RevenueCat Web Billing / Stripe)
                  the Apple cancellation path and EULA don't apply, so we show
                  the billing-portal cancellation route instead. */}
              <div className="mt-3 pt-3 border-t border-border-subtle space-y-1.5">
                <p className="text-[11px] text-text-muted leading-snug">
                  <span className="font-semibold text-text-secondary">TeeSync Pro</span> · Auto-renewing subscription, monthly or annual.
                </p>
                <p className="text-[11px] text-text-muted leading-snug">
                  {isNativeIOS
                    ? 'Your subscription renews automatically at the end of each period unless cancelled at least 24 hours before it ends. Cancel any time in iOS Settings → Apple Account → Subscriptions.'
                    : 'Your subscription renews automatically at the end of each period unless cancelled. Cancel any time from Manage Subscription in your account settings.'}
                </p>
                <p className="text-[11px] text-text-muted leading-snug">
                  By subscribing you agree to our{' '}
                  <Link to="/terms" className="underline hover:text-text-secondary">Terms of Use</Link>
                  {isNativeIOS ? (
                    <>
                      {', the '}
                      <a
                        href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-text-secondary"
                      >
                        Apple EULA
                      </a>
                      {', and our '}
                    </>
                  ) : (
                    ' and our '
                  )}
                  <Link to="/privacy" className="underline hover:text-text-secondary">Privacy Policy</Link>.
                </p>
              </div>
            </motion.div>
          )}

          <div className="bg-card rounded-xl p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-text-primary mb-3">Everything in Pro</h2>
            <ul className="space-y-2">
              {PRO_FEATURES.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-text-primary">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleRestore}
              disabled={restoring}
              className="inline-flex items-center gap-2 text-xs text-text-secondary hover:text-text-primary disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${restoring ? 'animate-spin' : ''}`} />
              {restoring ? (isIOSNative ? 'Restoring…' : 'Refreshing…') : restoreLabel}
            </button>
          </div>
        </main>
        </div>
      </div>
    </PageTransition>
  );
}

export default Upgrade;
